import {
  createRootRoute,
  Navigate,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { TopBar } from "../components/TopBar";
import { AuthProvider, canAccessArea, useAuth } from "../lib/auth";
import { ForcePasswordChange } from "../components/ForcePasswordChange";
import { TOOLS } from "../lib/atomika-tools";

export const Route = createRootRoute({
  component: RootLayout,
});

const PUBLIC_PATHS = new Set(["/velkomin", "/login", "/gleymt-lykilord"]);

const AREA_BY_PATH: Record<string, string> = Object.fromEntries(
  TOOLS.map((t) => [t.route, t.id]),
);

function areaForPath(pathname: string): string | undefined {
  const exact = AREA_BY_PATH[pathname];
  if (exact) return exact;
  for (const [route, id] of Object.entries(AREA_BY_PATH)) {
    if (pathname.startsWith(`${route}/`)) return id;
  }
  return undefined;
}

function RootLayout() {
  return (
    <AuthProvider>
      <RootGate />
    </AuthProvider>
  );
}

function RootGate() {
  const { user, loading } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isPublic = PUBLIC_PATHS.has(pathname);

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-canvas text-sm text-muted-3">
        Hleð…
      </div>
    );
  }

  if (!user) {
    return isPublic ? <Outlet /> : <Navigate to="/velkomin" />;
  }

  if (isPublic) {
    return <Navigate to="/" />;
  }

  if (user.mustChangePassword) {
    return <ForcePasswordChange user={user} />;
  }

  const requiredArea = areaForPath(pathname);
  if (requiredArea && !canAccessArea(user, requiredArea)) {
    return (
      <div className="min-h-screen bg-canvas font-sans text-ink">
        <TopBar />
        <div className="mx-auto max-w-lg px-6 py-24 text-center">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-line bg-white">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#5B6B85"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-ink">Enginn aðgangur</h1>
          <p className="mt-2 text-sm text-muted-2">
            Þú hefur ekki aðgang að þessu svæði. Hafðu samband við stjórnanda ef
            þú telur þig eiga að hafa aðgang.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas font-sans text-ink">
      <TopBar />
      <Outlet />
    </div>
  );
}
